import {
  UserRepository,
  CourseRepository,
  QuizRepository,
  CertificateRepository,
  ResourceRepository,
  LessonRepository,
  ModuleRepository,
  MessageRepository,
  NotificationRepository,
  TagRepository,
  SubscriptionRepository,
  AnalyticsRepository,
  AdminAuditRepository,
} from '../repository';

import { CertificationsService } from '../learning/certifications.service';
import { AdminUserManagementService } from './user-management.service';

import {
  AuthenticationUseCase,
  CertificationUseCase,
  QuizUseCase,
  ConnectUseCase,
  CurriculumUseCase,
  ModuleUseCase,
  ResourcesUseCase,
  SubscriptionUseCase,
  ReviewsUseCase,
  NotificationsUseCase,
  AnalyticsUseCase
} from '../usecases';

export class AdminService {
  public userManagement: AdminUserManagementService;

  constructor(
    private userRepo: UserRepository,
    private courseRepo: CourseRepository,
    private quizRepo: QuizRepository,
    private certificateRepo: CertificateRepository,
    private resourceRepo: ResourceRepository,
    private lessonRepo: LessonRepository,
    private moduleRepo: ModuleRepository,
    private messageRepo: MessageRepository,
    private notificationRepo: NotificationRepository,
    private tagRepo: TagRepository,
    private subscriptionRepo: SubscriptionRepository,
    private analyticsRepo: AnalyticsRepository,
    private adminAuditRepo: AdminAuditRepository,
    private certificationsService: CertificationsService,
    private authenticationUseCase: AuthenticationUseCase,
    private certificationUseCase: CertificationUseCase,
    private quizUseCase: QuizUseCase,
    private connectUseCase: ConnectUseCase,
    private curriculumUseCase: CurriculumUseCase,
    private moduleUseCase: ModuleUseCase,
    private resourcesUseCase: ResourcesUseCase,
    private subscriptionUseCase: SubscriptionUseCase,
    private reviewsUseCase: ReviewsUseCase,
    private notificationsUseCase: NotificationsUseCase,
    private analyticsUseCase: AnalyticsUseCase
  ) {
    console.log('[AdminService] Constructor - Initialized with all use cases and repositories');
    
    // Initialize user management service
    this.userManagement = new AdminUserManagementService(this.userRepo, this.adminAuditRepo);
  }

  // User Management - Admin Level (using AuthenticationUseCase)
  async getAllUsers(adminUserId?: string, limit?: number) {
    console.log('[AdminService.getAllUsers] START - AdminUserId:', adminUserId, 'Limit:', limit);
    try {
      console.log('[AdminService.getAllUsers] Calling authenticationUseCase.getAllUsers');
      // For admin service, we bypass the admin check since this is already an admin context
      if (!adminUserId) {
        // Fallback to direct repository call if no admin user ID provided (for backward compatibility)
        console.log('[AdminService.getAllUsers] No adminUserId provided, using direct repository call');
        const result = await this.userRepo.getAllUsers(limit);
        console.log('[AdminService.getAllUsers] SUCCESS (direct) - Retrieved', result.length, 'users');
        return result;
      }
      
      const result = await this.authenticationUseCase.getAllUsers(adminUserId, { limit });
      console.log('[AdminService.getAllUsers] SUCCESS (use case) - Retrieved', result.users.length, 'users');
      return result.users;
    } catch (error) {
      console.error('[AdminService.getAllUsers] ERROR:', error);
      throw error;
    }
  }

  async getUserById(userId: string) {
    console.log('[AdminService.getUserById] START - UserId:', userId);
    try {
      console.log('[AdminService.getUserById] Calling userRepo.getUserById');
      const result = await this.userRepo.getUserById(userId);
      console.log('[AdminService.getUserById] SUCCESS - User found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getUserById] ERROR:', error);
      throw error;
    }
  }

  async createUser(userData: any) {
    console.log('[AdminService.createUser] START - UserData:', userData);
    try {
      console.log('[AdminService.createUser] Calling userRepo.createUser');
      const result = await this.userRepo.createUser(userData);
      console.log('[AdminService.createUser] SUCCESS - Created user:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createUser] ERROR:', error);
      throw error;
    }
  }

  async updateUser(userId: string, updateData: any) {
    console.log('[AdminService.updateUser] START - UserId:', userId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateUser] Calling userRepo.updateUser');
      const result = await this.userRepo.updateUser(userId, updateData);
      console.log('[AdminService.updateUser] SUCCESS - Updated user:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.updateUser] ERROR:', error);
      throw error;
    }
  }

  async deleteUser(userId: string) {
    console.log('[AdminService.deleteUser] START - UserId:', userId);
    try {
      console.log('[AdminService.deleteUser] Calling userRepo.deleteUser');
      await this.userRepo.deleteUser(userId);
      console.log('[AdminService.deleteUser] SUCCESS - Deleted user:', userId);
    } catch (error) {
      console.error('[AdminService.deleteUser] ERROR:', error);
      throw error;
    }
  }

  // Course Management - Admin Level
  async getAllCourses(limit?: number) {
    console.log('[AdminService.getAllCourses] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllCourses] Calling certificationsService.getAllCourses');
      const result = await this.certificationsService.getAllCourses(limit);
      console.log('[AdminService.getAllCourses] SUCCESS - Retrieved', result.courses.length, 'courses');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllCourses] ERROR:', error);
      throw error;
    }
  }

  async getCourseById(courseId: string) {
    console.log('[AdminService.getCourseById] START - CourseId:', courseId);
    try {
      console.log('[AdminService.getCourseById] Calling courseRepo.getCourseById');
      const result = await this.courseRepo.getCourseById(courseId);
      console.log('[AdminService.getCourseById] SUCCESS - Course found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getCourseById] ERROR:', error);
      throw error;
    }
  }

  async createCourse(courseData: any) {
    console.log('[AdminService.createCourse] START - CourseData:', courseData);
    try {
      console.log('[AdminService.createCourse] Calling courseRepo.createCourse');
      const result = await this.courseRepo.createCourse(courseData);
      console.log('[AdminService.createCourse] SUCCESS - Created course:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createCourse] ERROR:', error);
      throw error;
    }
  }

  async updateCourse(courseId: string, updateData: any) {
    console.log('[AdminService.updateCourse] START - CourseId:', courseId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateCourse] Calling courseRepo.updateCourse');
      const result = await this.courseRepo.updateCourse(courseId, updateData);
      console.log('[AdminService.updateCourse] SUCCESS - Updated course:', courseId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateCourse] ERROR:', error);
      throw error;
    }
  }

  async deleteCourse(courseId: string) {
    console.log('[AdminService.deleteCourse] START - CourseId:', courseId);
    try {
      console.log('[AdminService.deleteCourse] Calling courseRepo.deleteCourse');
      await this.courseRepo.deleteCourse(courseId);
      console.log('[AdminService.deleteCourse] SUCCESS - Deleted course:', courseId);
    } catch (error) {
      console.error('[AdminService.deleteCourse] ERROR:', error);
      throw error;
    }
  }

  // Quiz Management - Admin Level
  async getAllQuizzes(limit?: number) {
    console.log('[AdminService.getAllQuizzes] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllQuizzes] Calling quizRepo.getAllQuizzes');
      const result = await this.quizRepo.getAllQuizzes(limit);
      console.log('[AdminService.getAllQuizzes] SUCCESS - Retrieved', result.length, 'quizzes');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllQuizzes] ERROR:', error);
      throw error;
    }
  }

  async getQuizById(quizId: string) {
    console.log('[AdminService.getQuizById] START - QuizId:', quizId);
    try {
      console.log('[AdminService.getQuizById] Calling quizRepo.getQuizById');
      const result = await this.quizRepo.getQuizById(quizId);
      console.log('[AdminService.getQuizById] SUCCESS - Quiz found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getQuizById] ERROR:', error);
      throw error;
    }
  }

  async createQuiz(quizData: any) {
    console.log('[AdminService.createQuiz] START - QuizData:', quizData);
    try {
      console.log('[AdminService.createQuiz] Calling quizRepo.createQuiz');
      const result = await this.quizRepo.createQuiz(quizData);
      console.log('[AdminService.createQuiz] SUCCESS - Created quiz:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createQuiz] ERROR:', error);
      throw error;
    }
  }

  async updateQuiz(quizId: string, updateData: any) {
    console.log('[AdminService.updateQuiz] START - QuizId:', quizId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateQuiz] Calling quizRepo.updateQuiz');
      const result = await this.quizRepo.updateQuiz(quizId, updateData);
      console.log('[AdminService.updateQuiz] SUCCESS - Updated quiz:', quizId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateQuiz] ERROR:', error);
      throw error;
    }
  }

  async deleteQuiz(quizId: string) {
    console.log('[AdminService.deleteQuiz] START - QuizId:', quizId);
    try {
      console.log('[AdminService.deleteQuiz] Calling quizRepo.deleteQuiz');
      await this.quizRepo.deleteQuiz(quizId);
      console.log('[AdminService.deleteQuiz] SUCCESS - Deleted quiz:', quizId);
    } catch (error) {
      console.error('[AdminService.deleteQuiz] ERROR:', error);
      throw error;
    }
  }


  // Certificate Management - Admin Level
  async getAllCertificates(limit?: number) {
    console.log('[AdminService.getAllCertificates] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllCertificates] Calling certificateRepo.getAllCertificates');
      const result = await this.certificateRepo.getAllCertificates(limit);
      console.log('[AdminService.getAllCertificates] SUCCESS - Retrieved', result.length, 'certificates');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllCertificates] ERROR:', error);
      throw error;
    }
  }

  async getCertificateById(certificateId: string) {
    console.log('[AdminService.getCertificateById] START - CertificateId:', certificateId);
    try {
      console.log('[AdminService.getCertificateById] Calling certificateRepo.getCertificateById');
      const result = await this.certificateRepo.getCertificateById(certificateId);
      console.log('[AdminService.getCertificateById] SUCCESS - Certificate found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getCertificateById] ERROR:', error);
      throw error;
    }
  }

  async createCertificate(certificateData: any) {
    console.log('[AdminService.createCertificate] START - CertificateData:', certificateData);
    try {
      console.log('[AdminService.createCertificate] Calling certificateRepo.createCertificate');
      const result = await this.certificateRepo.createCertificate(certificateData);
      console.log('[AdminService.createCertificate] SUCCESS - Created certificate:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createCertificate] ERROR:', error);
      throw error;
    }
  }

  async updateCertificate(certificateId: string, updateData: any) {
    console.log('[AdminService.updateCertificate] START - CertificateId:', certificateId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateCertificate] Calling certificateRepo.updateCertificate');
      const result = await this.certificateRepo.updateCertificate(certificateId, updateData);
      console.log('[AdminService.updateCertificate] SUCCESS - Updated certificate:', certificateId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateCertificate] ERROR:', error);
      throw error;
    }
  }

  async deleteCertificate(certificateId: string) {
    console.log('[AdminService.deleteCertificate] START - CertificateId:', certificateId);
    try {
      console.log('[AdminService.deleteCertificate] Calling certificateRepo.deleteCertificate');
      await this.certificateRepo.deleteCertificate(certificateId);
      console.log('[AdminService.deleteCertificate] SUCCESS - Deleted certificate:', certificateId);
    } catch (error) {
      console.error('[AdminService.deleteCertificate] ERROR:', error);
      throw error;
    }
  }

  // Resource Management - Admin Level
  async getAllResources(limit?: number) {
    console.log('[AdminService.getAllResources] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllResources] Calling resourceRepo.getAllResources');
      const result = await this.resourceRepo.getAllResources(limit);
      console.log('[AdminService.getAllResources] SUCCESS - Retrieved', result.length, 'resources');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllResources] ERROR:', error);
      throw error;
    }
  }

  async getResourceById(resourceId: string) {
    console.log('[AdminService.getResourceById] START - ResourceId:', resourceId);
    try {
      console.log('[AdminService.getResourceById] Calling resourceRepo.getResourceById');
      const result = await this.resourceRepo.getResourceById(resourceId);
      console.log('[AdminService.getResourceById] SUCCESS - Resource found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getResourceById] ERROR:', error);
      throw error;
    }
  }

  async createResource(resourceData: any) {
    console.log('[AdminService.createResource] START - ResourceData:', resourceData);
    try {
      if (!resourceData.instructorId) {
        console.log('[AdminService.createResource] No instructorId provided, this should be set by the form');
        throw new Error('instructorId is required when creating a resource');
      }
      console.log('[AdminService.createResource] Calling resourceRepo.createResource');
      const result = await this.resourceRepo.createResource(resourceData);
      console.log('[AdminService.createResource] SUCCESS - Created resource:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createResource] ERROR:', error);
      throw error;
    }
  }

  async updateResource(resourceId: string, updateData: any) {
    console.log('[AdminService.updateResource] START - ResourceId:', resourceId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateResource] Calling resourceRepo.updateResource');
      const result = await this.resourceRepo.updateResource(resourceId, updateData);
      console.log('[AdminService.updateResource] SUCCESS - Updated resource:', resourceId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateResource] ERROR:', error);
      throw error;
    }
  }

  async deleteResource(resourceId: string) {
    console.log('[AdminService.deleteResource] START - ResourceId:', resourceId);
    try {
      console.log('[AdminService.deleteResource] Calling resourceRepo.deleteResource');
      await this.resourceRepo.deleteResource(resourceId);
      console.log('[AdminService.deleteResource] SUCCESS - Deleted resource:', resourceId);
    } catch (error) {
      console.error('[AdminService.deleteResource] ERROR:', error);
      throw error;
    }
  }

  // Resource Category Management - Admin Level
  async getAllResourceCategories(limit?: number) {
    console.log('[AdminService.getAllResourceCategories] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllResourceCategories] Calling resourceRepo.getAllResourceCategories');
      const result = await this.resourceRepo.getAllResourceCategories(limit);
      console.log('[AdminService.getAllResourceCategories] SUCCESS - Retrieved', result.length, 'resource categories');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllResourceCategories] ERROR:', error);
      throw error;
    }
  }

  async createResourceCategory(categoryData: any) {
    console.log('[AdminService.createResourceCategory] START - CategoryData:', categoryData);
    try {
      console.log('[AdminService.createResourceCategory] Calling resourceRepo.createResourceCategory');
      const result = await this.resourceRepo.createResourceCategory(categoryData);
      console.log('[AdminService.createResourceCategory] SUCCESS - Created resource category');
      return result;
    } catch (error) {
      console.error('[AdminService.createResourceCategory] ERROR:', error);
      throw error;
    }
  }

  async updateResourceCategory(categoryId: string, updateData: any) {
    console.log('[AdminService.updateResourceCategory] START - CategoryId:', categoryId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateResourceCategory] Calling resourceRepo.updateResourceCategory');
      const result = await this.resourceRepo.updateResourceCategory(categoryId, updateData);
      console.log('[AdminService.updateResourceCategory] SUCCESS - Updated resource category');
      return result;
    } catch (error) {
      console.error('[AdminService.updateResourceCategory] ERROR:', error);
      throw error;
    }
  }

  async deleteResourceCategory(categoryId: string) {
    console.log('[AdminService.deleteResourceCategory] START - CategoryId:', categoryId);
    try {
      console.log('[AdminService.deleteResourceCategory] Calling resourceRepo.deleteResourceCategory');
      await this.resourceRepo.deleteResourceCategory(categoryId);
      console.log('[AdminService.deleteResourceCategory] SUCCESS - Deleted resource category');
      return { success: true };
    } catch (error) {
      console.error('[AdminService.deleteResourceCategory] ERROR:', error);
      throw error;
    }
  }

  // Lesson Management - Admin Level
  async getAllLessons(limit?: number) {
    console.log('[AdminService.getAllLessons] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllLessons] Calling lessonRepo.getAllLessons');
      const result = await this.lessonRepo.getAllLessons(limit);
      console.log('[AdminService.getAllLessons] SUCCESS - Retrieved', result.length, 'lessons');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllLessons] ERROR:', error);
      throw error;
    }
  }

  async getLessonById(lessonId: string) {
    console.log('[AdminService.getLessonById] START - LessonId:', lessonId);
    try {
      console.log('[AdminService.getLessonById] Calling lessonRepo.getLessonById');
      const result = await this.lessonRepo.getLessonById(lessonId);
      console.log('[AdminService.getLessonById] SUCCESS - Lesson found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getLessonById] ERROR:', error);
      throw error;
    }
  }

  async createLesson(lessonData: any) {
    console.log('[AdminService.createLesson] START - LessonData:', lessonData);
    try {
      console.log('[AdminService.createLesson] Calling lessonRepo.createLesson');
      const result = await this.lessonRepo.createLesson(lessonData);
      console.log('[AdminService.createLesson] SUCCESS - Created lesson:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createLesson] ERROR:', error);
      throw error;
    }
  }

  async updateLesson(lessonId: string, updateData: any) {
    console.log('[AdminService.updateLesson] START - LessonId:', lessonId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateLesson] Calling lessonRepo.updateLesson');
      const result = await this.lessonRepo.updateLesson(lessonId, updateData);
      console.log('[AdminService.updateLesson] SUCCESS - Updated lesson:', lessonId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateLesson] ERROR:', error);
      throw error;
    }
  }

  async deleteLesson(lessonId: string) {
    console.log('[AdminService.deleteLesson] START - LessonId:', lessonId);
    try {
      console.log('[AdminService.deleteLesson] Calling lessonRepo.deleteLesson');
      await this.lessonRepo.deleteLesson(lessonId);
      console.log('[AdminService.deleteLesson] SUCCESS - Deleted lesson:', lessonId);
    } catch (error) {
      console.error('[AdminService.deleteLesson] ERROR:', error);
      throw error;
    }
  }

  // Module Management - Admin Level
  async getAllModules(limit?: number) {
    console.log('[AdminService.getAllModules] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllModules] Calling moduleRepo.getAllModules');
      const result = await this.moduleRepo.getAllModules(limit);
      console.log('[AdminService.getAllModules] SUCCESS - Retrieved', result.length, 'modules');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllModules] ERROR:', error);
      throw error;
    }
  }

  async getModuleById(moduleId: string) {
    console.log('[AdminService.getModuleById] START - ModuleId:', moduleId);
    try {
      console.log('[AdminService.getModuleById] Calling moduleRepo.getModuleById');
      const result = await this.moduleRepo.getModuleById(moduleId);
      console.log('[AdminService.getModuleById] SUCCESS - Module found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getModuleById] ERROR:', error);
      throw error;
    }
  }

  async createModule(moduleData: any) {
    console.log('[AdminService.createModule] START - ModuleData:', moduleData);
    try {
      console.log('[AdminService.createModule] Calling moduleRepo.createModule');
      const result = await this.moduleRepo.createModule(moduleData);
      console.log('[AdminService.createModule] SUCCESS - Created module:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createModule] ERROR:', error);
      throw error;
    }
  }

  async updateModule(moduleId: string, updateData: any) {
    console.log('[AdminService.updateModule] START - ModuleId:', moduleId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateModule] Calling moduleRepo.updateModule');
      const result = await this.moduleRepo.updateModule(moduleId, updateData);
      console.log('[AdminService.updateModule] SUCCESS - Updated module:', moduleId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateModule] ERROR:', error);
      throw error;
    }
  }

  async deleteModule(moduleId: string) {
    console.log('[AdminService.deleteModule] START - ModuleId:', moduleId);
    try {
      console.log('[AdminService.deleteModule] Calling moduleRepo.deleteModule');
      await this.moduleRepo.deleteModule(moduleId);
      console.log('[AdminService.deleteModule] SUCCESS - Deleted module:', moduleId);
    } catch (error) {
      console.error('[AdminService.deleteModule] ERROR:', error);
      throw error;
    }
  }

  // Message Management - Admin Level
  async getAllMessages(limit?: number) {
    console.log('[AdminService.getAllMessages] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllMessages] Calling messageRepo.getAllMessages');
      const result = await this.messageRepo.getAllMessages(limit);
      console.log('[AdminService.getAllMessages] SUCCESS - Retrieved', result.length, 'messages');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllMessages] ERROR:', error);
      throw error;
    }
  }

  async getMessageById(messageId: string) {
    console.log('[AdminService.getMessageById] START - MessageId:', messageId);
    try {
      console.log('[AdminService.getMessageById] Calling messageRepo.getMessageById');
      const result = await this.messageRepo.getMessageById(messageId);
      console.log('[AdminService.getMessageById] SUCCESS - Message found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getMessageById] ERROR:', error);
      throw error;
    }
  }

  async createMessage(messageData: any) {
    console.log('[AdminService.createMessage] START - MessageData:', messageData);
    try {
      console.log('[AdminService.createMessage] Calling messageRepo.createMessage');
      const result = await this.messageRepo.createMessage(messageData);
      console.log('[AdminService.createMessage] SUCCESS - Created message:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createMessage] ERROR:', error);
      throw error;
    }
  }

  async updateMessage(messageId: string, updateData: any) {
    console.log('[AdminService.updateMessage] START - MessageId:', messageId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateMessage] Calling messageRepo.updateMessage');
      const result = await this.messageRepo.updateMessage(messageId, updateData);
      console.log('[AdminService.updateMessage] SUCCESS - Updated message:', messageId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateMessage] ERROR:', error);
      throw error;
    }
  }

  async deleteMessage(messageId: string) {
    console.log('[AdminService.deleteMessage] START - MessageId:', messageId);
    try {
      console.log('[AdminService.deleteMessage] Calling messageRepo.deleteMessage');
      await this.messageRepo.deleteMessage(messageId);
      console.log('[AdminService.deleteMessage] SUCCESS - Deleted message:', messageId);
    } catch (error) {
      console.error('[AdminService.deleteMessage] ERROR:', error);
      throw error;
    }
  }

  // Notification Management - Admin Level
  async getAllNotifications(limit?: number) {
    console.log('[AdminService.getAllNotifications] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllNotifications] Calling notificationRepo.getAllNotifications');
      const result = await this.notificationRepo.getAllNotifications(limit);
      console.log('[AdminService.getAllNotifications] SUCCESS - Retrieved', result.length, 'notifications');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllNotifications] ERROR:', error);
      throw error;
    }
  }

  async getNotificationById(notificationId: string) {
    console.log('[AdminService.getNotificationById] START - NotificationId:', notificationId);
    try {
      console.log('[AdminService.getNotificationById] Calling notificationRepo.getNotificationById');
      const result = await this.notificationRepo.getNotificationById(notificationId);
      console.log('[AdminService.getNotificationById] SUCCESS - Notification found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getNotificationById] ERROR:', error);
      throw error;
    }
  }

  async createNotification(notificationData: any) {
    console.log('[AdminService.createNotification] START - NotificationData:', notificationData);
    try {
      console.log('[AdminService.createNotification] Calling notificationRepo.createNotification');
      const result = await this.notificationRepo.createNotification(notificationData);
      console.log('[AdminService.createNotification] SUCCESS - Created notification:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createNotification] ERROR:', error);
      throw error;
    }
  }

  async updateNotification(notificationId: string, updateData: any) {
    console.log('[AdminService.updateNotification] START - NotificationId:', notificationId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateNotification] Calling notificationRepo.updateNotification');
      const result = await this.notificationRepo.updateNotification(notificationId, updateData);
      console.log('[AdminService.updateNotification] SUCCESS - Updated notification:', notificationId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateNotification] ERROR:', error);
      throw error;
    }
  }

  async deleteNotification(notificationId: string) {
    console.log('[AdminService.deleteNotification] START - NotificationId:', notificationId);
    try {
      console.log('[AdminService.deleteNotification] Calling notificationRepo.deleteNotification');
      await this.notificationRepo.deleteNotification(notificationId);
      console.log('[AdminService.deleteNotification] SUCCESS - Deleted notification:', notificationId);
    } catch (error) {
      console.error('[AdminService.deleteNotification] ERROR:', error);
      throw error;
    }
  }

  // Tag Management - Admin Level
  async getAllTags(limit?: number) {
    console.log('[AdminService.getAllTags] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllTags] Calling tagRepo.getAllTags');
      const result = await this.tagRepo.getAllTags(limit);
      console.log('[AdminService.getAllTags] SUCCESS - Retrieved', result.length, 'tags');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllTags] ERROR:', error);
      throw error;
    }
  }

  async getTagById(tagId: string) {
    console.log('[AdminService.getTagById] START - TagId:', tagId);
    try {
      console.log('[AdminService.getTagById] Calling tagRepo.getTagById');
      const result = await this.tagRepo.getTagById(tagId);
      console.log('[AdminService.getTagById] SUCCESS - Tag found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getTagById] ERROR:', error);
      throw error;
    }
  }

  async createTag(tagData: any) {
    console.log('[AdminService.createTag] START - TagData:', tagData);
    try {
      console.log('[AdminService.createTag] Calling tagRepo.createTag');
      const result = await this.tagRepo.createTag(tagData);
      console.log('[AdminService.createTag] SUCCESS - Created tag:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createTag] ERROR:', error);
      throw error;
    }
  }

  async updateTag(tagId: string, updateData: any) {
    console.log('[AdminService.updateTag] START - TagId:', tagId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateTag] Calling tagRepo.updateTag');
      const result = await this.tagRepo.updateTag(tagId, updateData);
      console.log('[AdminService.updateTag] SUCCESS - Updated tag:', tagId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateTag] ERROR:', error);
      throw error;
    }
  }

  async deleteTag(tagId: string) {
    console.log('[AdminService.deleteTag] START - TagId:', tagId);
    try {
      console.log('[AdminService.deleteTag] Calling tagRepo.deleteTag');
      await this.tagRepo.deleteTag(tagId);
      console.log('[AdminService.deleteTag] SUCCESS - Deleted tag:', tagId);
    } catch (error) {
      console.error('[AdminService.deleteTag] ERROR:', error);
      throw error;
    }
  }

  // Subscription Management - Admin Level
  async getAllSubscriptions(limit?: number) {
    console.log('[AdminService.getAllSubscriptions] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllSubscriptions] Calling subscriptionRepo.getAllSubscriptions');
      const result = await this.subscriptionRepo.getAllSubscriptions(limit);
      console.log('[AdminService.getAllSubscriptions] SUCCESS - Retrieved', result.length, 'subscriptions');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllSubscriptions] ERROR:', error);
      throw error;
    }
  }

  async getSubscriptionById(subscriptionId: string) {
    console.log('[AdminService.getSubscriptionById] START - SubscriptionId:', subscriptionId);
    try {
      console.log('[AdminService.getSubscriptionById] Calling subscriptionRepo.getSubscriptionById');
      const result = await this.subscriptionRepo.getSubscriptionById(subscriptionId);
      console.log('[AdminService.getSubscriptionById] SUCCESS - Subscription found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[AdminService.getSubscriptionById] ERROR:', error);
      throw error;
    }
  }

  async createSubscription(subscriptionData: any) {
    console.log('[AdminService.createSubscription] START - SubscriptionData:', subscriptionData);
    try {
      console.log('[AdminService.createSubscription] Calling subscriptionRepo.createSubscription');
      const result = await this.subscriptionRepo.createSubscription(subscriptionData);
      console.log('[AdminService.createSubscription] SUCCESS - Created subscription:', result.id);
      return result;
    } catch (error) {
      console.error('[AdminService.createSubscription] ERROR:', error);
      throw error;
    }
  }

  async updateSubscription(subscriptionId: string, updateData: any) {
    console.log('[AdminService.updateSubscription] START - SubscriptionId:', subscriptionId, 'UpdateData:', updateData);
    try {
      console.log('[AdminService.updateSubscription] Calling subscriptionRepo.updateSubscription');
      const result = await this.subscriptionRepo.updateSubscription(subscriptionId, updateData);
      console.log('[AdminService.updateSubscription] SUCCESS - Updated subscription:', subscriptionId);
      return result;
    } catch (error) {
      console.error('[AdminService.updateSubscription] ERROR:', error);
      throw error;
    }
  }

  async deleteSubscription(subscriptionId: string) {
    console.log('[AdminService.deleteSubscription] START - SubscriptionId:', subscriptionId);
    try {
      console.log('[AdminService.deleteSubscription] Calling subscriptionRepo.deleteSubscription');
      await this.subscriptionRepo.deleteSubscription(subscriptionId);
      console.log('[AdminService.deleteSubscription] SUCCESS - Deleted subscription:', subscriptionId);
    } catch (error) {
      console.error('[AdminService.deleteSubscription] ERROR:', error);
      throw error;
    }
  }

  // Analytics - Admin Level
  async getAllAnalytics(limit?: number) {
    console.log('[AdminService.getAllAnalytics] START - Limit:', limit);
    try {
      console.log('[AdminService.getAllAnalytics] Calling analyticsRepo.getAllAnalytics');
      const result = await this.analyticsRepo.getAllAnalytics(limit);
      console.log('[AdminService.getAllAnalytics] SUCCESS - Retrieved', result.length, 'analytics records');
      return result;
    } catch (error) {
      console.error('[AdminService.getAllAnalytics] ERROR:', error);
      throw error;
    }
  }

  // Complex operations using use cases
  async createCertificationWorkflow(data: any) {
    console.log('[AdminService.createCertificationWorkflow] START - Data:', data);
    try {
      console.log('[AdminService.createCertificationWorkflow] Executing certification use case workflow');
      // Use certification use case for complex certificate creation
      const result = await this.certificationUseCase.createCertification(
        data.courseData ?? data,
        data.modules ?? []
      );
      console.log('[AdminService.createCertificationWorkflow] SUCCESS - Certification workflow completed');
      return result;
    } catch (error) {
      console.error('[AdminService.createCertificationWorkflow] ERROR:', error);
      throw error;
    }
  }

  async createQuizWithQuestions(data: any) {
    console.log('[AdminService.createQuizWithQuestions] START - Data:', data);
    try {
      console.log('[AdminService.createQuizWithQuestions] Executing quiz creation workflow');
      // Use quiz use case for complex quiz creation
      const result = await this.quizUseCase.createQuizWithQuestions(
        data.quizData ?? data,
        data.questions ?? []
      );
      console.log('[AdminService.createQuizWithQuestions] SUCCESS - Quiz with questions created');
      return result;
    } catch (error) {
      console.error('[AdminService.createQuizWithQuestions] ERROR:', error);
      throw error;
    }
  }

  async createModuleWithLessons(data: any) {
    console.log('[AdminService.createModuleWithLessons] START - Data:', data);
    try {
      console.log('[AdminService.createModuleWithLessons] Executing module creation workflow');
      // Use module use case for complex module creation
      const result = await this.moduleUseCase.createCompleteModule(
        data.moduleData ?? data,
        data.lessons ?? [],
        data.quizzes ?? []
      );
      console.log('[AdminService.createModuleWithLessons] SUCCESS - Module with lessons created');
      return result;
    } catch (error) {
      console.error('[AdminService.createModuleWithLessons] ERROR:', error);
      throw error;
    }
  }


  async manageSubscription(data: any) {
    console.log('[AdminService.manageSubscription] START - Data:', data);
    try {
      console.log('[AdminService.manageSubscription] Executing subscription management workflow');
      // Use subscription use case for complex subscription operations
      const result = await this.subscriptionUseCase.changeSubscription(
        data.userId,
        data.newPlan ?? {
          tier: data.tier,
          frequency: data.frequency,
          couponCode: data.couponCode,
        }
      );
      console.log('[AdminService.manageSubscription] SUCCESS - Subscription managed');
      return result;
    } catch (error) {
      console.error('[AdminService.manageSubscription] ERROR:', error);
      throw error;
    }
  }

  async sendBulkNotifications(data: any) {
    console.log('[AdminService.sendBulkNotifications] START - Data:', data);
    try {
      console.log('[AdminService.sendBulkNotifications] Executing bulk notifications workflow');
      // Use notifications use case for bulk operations
      const result = await this.notificationsUseCase.sendBulkNotifications(
        data.userIds ?? [],
        data.notificationData ?? {
          type: data.type,
          category: data.category,
          title: data.title,
          content: data.content,
          metadata: data.metadata,
        },
        data.channels
      );
      console.log('[AdminService.sendBulkNotifications] SUCCESS - Bulk notifications sent');
      return result;
    } catch (error) {
      console.error('[AdminService.sendBulkNotifications] ERROR:', error);
      throw error;
    }
  }

  async generateAnalyticsReport(data: any) {
    console.log('[AdminService.generateAnalyticsReport] START - Data:', data);
    try {
      console.log('[AdminService.generateAnalyticsReport] Executing analytics report generation');
      // Use analytics use case for report generation
      const result = await this.analyticsUseCase.exportAnalyticsData(data);
      console.log('[AdminService.generateAnalyticsReport] SUCCESS - Analytics report generated');
      return result;
    } catch (error) {
      console.error('[AdminService.generateAnalyticsReport] ERROR:', error);
      throw error;
    }
  }
}
